import { useUser } from '@descope/react-sdk'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useListUsers } from '@/api/list-users.hook'

import { StringNameAvatar } from '@/components/lemmala-design-system/avatar'
import { Button } from '@/components/lemmala-design-system/button/button'
import { DropdownSelect } from '@/components/lemmala-design-system/dropdown/dropdown-select'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { assigneeLabels } from '@/components/shared/assignee/labels'
import type { AssigneePerson } from '@/components/shared/assignee/types'
import {
  assigneePeopleContentKey,
  idsFromKey,
  idsKey,
  mergeAssigneePeople,
  sortAssigneeItems,
} from '@/components/shared/assignee/utils'
import { personFilterLabels } from '@/components/shared/person-filter/labels'

interface AssigneeDropdownProps {
  trigger: React.ReactNode
  popoverAlign?: 'start' | 'center' | 'end'
  open: boolean
  onOpenChange: (open: boolean) => void
  // Also fills options for assignees missing from ListUsers.
  selectedUsers: AssigneePerson[]
  onAssign: (userId: string) => Promise<unknown>
  onUnassign: (userId: string) => Promise<unknown>
}

export const AssigneeDropdown = ({
  trigger,
  popoverAlign = 'start',
  open,
  onOpenChange,
  selectedUsers,
  onAssign,
  onUnassign,
}: AssigneeDropdownProps) => {
  const { toast } = useToast()
  const { user: currentUser } = useUser()
  const { data: allUsers, isLoading: isUsersLoading } = useListUsers()

  const selectedIdsKey = idsKey(selectedUsers.map((user) => user.id))
  const selectedUserIds = useMemo(() => idsFromKey(selectedIdsKey), [selectedIdsKey])
  const selectedUsersContentKey = assigneePeopleContentKey(selectedUsers)

  const [sortIdsKey, setSortIdsKey] = useState(selectedIdsKey)
  // Freeze merge seed while open so unchecking a ListUsers-orphan does not drop the row.
  const [optionSelectedUsers, setOptionSelectedUsers] = useState(selectedUsers)

  useEffect(() => {
    if (!open) {
      // oxlint-disable-next-line react/set-state-in-effect
      setSortIdsKey(selectedIdsKey)
      // oxlint-disable-next-line react/set-state-in-effect
      setOptionSelectedUsers((prev) =>
        assigneePeopleContentKey(prev) === selectedUsersContentKey ? prev : selectedUsers,
      )
    }
  }, [open, selectedIdsKey, selectedUsers, selectedUsersContentKey])

  const userItems = useMemo(() => {
    const people = mergeAssigneePeople(allUsers, optionSelectedUsers)
    const items = people.map((user) => {
      const label = user.name || user.email || assigneeLabels.unknownUser
      return {
        id: user.id,
        label,
        value: user.email,
        disabled: isUsersLoading,
        labelSuffix: user.id === currentUser?.userId ? personFilterLabels.youSuffix : undefined,
        icon: <StringNameAvatar name={label} size='small' />,
      }
    })
    return sortAssigneeItems(items, idsFromKey(sortIdsKey), currentUser?.userId)
  }, [allUsers, currentUser?.userId, isUsersLoading, optionSelectedUsers, sortIdsKey])

  const handleMultiValueChange = useCallback(
    (newSelectedIds: string[]) => {
      const addedIds = newSelectedIds.filter((id) => !selectedUserIds.includes(id))
      const removedIds = selectedUserIds.filter((id) => !newSelectedIds.includes(id))

      const apiCalls = [
        ...addedIds.map((userId) => onAssign(userId)),
        ...removedIds.map((userId) => onUnassign(userId)),
      ]

      Promise.all(apiCalls).catch((error) => {
        console.error('Failed to update assignees:', error)
        toast({
          variant: 'destructive',
          title: assigneeLabels.updateErrorTitle,
          description: assigneeLabels.updateErrorDescription,
        })
      })
    },
    [selectedUserIds, onAssign, onUnassign, toast],
  )

  const handleClearAll = useCallback(() => {
    handleMultiValueChange([])
  }, [handleMultiValueChange])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        setSortIdsKey(selectedIdsKey)
        setOptionSelectedUsers(selectedUsers)
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange, selectedIdsKey, selectedUsers],
  )

  return (
    <DropdownSelect
      items={userItems}
      multi
      search
      searchPlaceholder={assigneeLabels.searchPlaceholder}
      selectedItems={selectedUserIds}
      onMultiValueChange={handleMultiValueChange}
      open={open}
      onOpenChange={handleOpenChange}
      popoverAlign={popoverAlign}
      trigger={trigger}
      isModal
      footerContent={
        <>
          <div className='mb-2 border-t border-main-border-default' />
          <Button
            variant='ghost'
            size='sm'
            onClick={handleClearAll}
            disabled={isUsersLoading}
            className={`justify-start text-label-small ${selectedUserIds.length ? 'text-main-foreground-accent' : 'text-main-foreground-disabled'}`}
          >
            {assigneeLabels.clearAll}
          </Button>
        </>
      }
      footerClassName={'px-2 pb-0 border-none'}
    />
  )
}
